import { SourceType } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Label,
  Input,
  useToasterContext,
  ActionButton,
  Form,
  ActionSubmit,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  Navigate,
  useNavigate,
} from "@tanstack/react-router"
import type {
  FocusEventHandler,
  ChangeEventHandler,
  FormEventHandler,
} from "react"
import {
  useCallback,
  useState,
} from "react"

import styles from "./Nightscout.module.scss"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import {
  useCreateNightscoutSource,
  useSources,
} from "src/hooks"
import {
  AddSourceErrorToast,
  AddSourceSuccessToast,
} from "src/toasts"
import {
  getSugarmateError,
  injectProps,
} from "src/utils"

const polyglotPrefix = "pages.settings.data_source.nightscout."
const urlId = "nightscout-url-input"
const tokenId = "nightscout-auth-token-input"

const defaultFormState = {
  url: "",
  token: "",
}

interface Props {
  isOnboarding?: boolean;
}

export function NightscoutContent(props: Props) {
  const createSource = useCreateNightscoutSource()
  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const { isOnboarding } = props
  const navigate = useNavigate()

  const [
    formState,
    setFormState,
  ] = useState<{
    url: string;
    token: string;
  }>(defaultFormState)

  const analyticsRef = useSegment()

  const onNightscoutUrlChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        setFormState({
          ...formState,
          url: e.target.value,
        })
      },
      [
        setFormState,
        formState,
      ],
    )

  const onNightscoutUrlBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      let value = e.target.value
      const regex = /^(http|https):\/\//

      if (!regex.test(value)) {
        value = `http://${value}`
      }

      setFormState({
        ...formState,
        url: value,
      })
    },
    [
      setFormState,
      formState,
    ],
  )

  const onNightscoutTokenChange: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        setFormState({
          ...formState,
          token: e.target.value,
        })
      },
      [
        setFormState,
        formState,
      ],
    )

  const onCancel = useCallback(
    () => {
      void navigate({
        to: isOnboarding ? "/onboarding/data_source" : "/account/data_source",
      })
    },
    [
      navigate,
      isOnboarding,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!createSource) {
            throw new Error("cannot add nightscout source")
          }

          await createSource({
            url: formState.url,
            token: formState.token,
          })

          void analyticsRef.current.track(
            "Data Source Added",
            {
              "Data Source": SourceType.Nightscout,
            },
          )

          showToast(
            "add-nightscout-source-success-toast",
            AddSourceSuccessToast,
          )

          void navigate({
            to: isOnboarding
              ? "/onboarding/data_source"
              : "/account/data_source",
          })
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            AddSourceErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "add-nightscout-source-error-toast",
            ErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      formState,
      createSource,
      showToast,
      analyticsRef,
      navigate,
      isOnboarding,
      setIsOverlayLoaderVisible,
    ],
  )

  const { data: sources } = useSources()

  // navigate to data source page if user already has a source
  if (sources && sources.length > 0) {
    return (
      <Navigate
        to={isOnboarding
          ? "/onboarding/data_source"
          : "/account/data_source"
        }
        params={{}}
        search={{}}
        replace
      />
    )
  }

  return (
    <Form
      className={styles.container}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}heading`)}
      </div>
      <div>
        <Label
          htmlFor={urlId}
          sizePreset="small"
          className={styles.nightscoutLabel}
        >
          {polyglot.t(`${polyglotPrefix}url_label`)}
        </Label>
        <Input
          id={urlId}
          placeholder={polyglot.t(`${polyglotPrefix}url_placeholder`)}
          type="url"
          className={styles.nightscoutURLInput}
          value={formState.url}
          onChange={onNightscoutUrlChange}
          onBlur={onNightscoutUrlBlur}
          required
        />
      </div>
      <div>
        <Label
          htmlFor={tokenId}
          sizePreset="small"
          className={styles.nightscoutLabel}
        >
          {polyglot.t(`${polyglotPrefix}token_label`)}
        </Label>
        <Input
          id={tokenId}
          placeholder={polyglot.t(`${polyglotPrefix}token_placeholder`)}
          type="text"
          className={styles.nightscoutTokenInput}
          value={formState.token}
          onChange={onNightscoutTokenChange}
        />
      </div>
      <div className={styles.buttonsContainer}>
        <ActionButton
          id="cancel-button"
          stylePreset="secondary"
          onClick={onCancel}
          className={styles.button}
        >
          {polyglot.t(`${polyglotPrefix}cancel_cta`)}
        </ActionButton>
        <ActionSubmit
          id="submit-button"
          className={styles.button}
          value={polyglot.t(`${polyglotPrefix}submit_cta`)}
        />
      </div>
    </Form>
  )
}
